/**
 * STEP CONTAINER COMPONENT
 * 
 * Wrapper that handles theme switching between Spec Ops and Elegant
 * Provides consistent layout and theming for each step
 */

import React from 'react';
import { getThemeForStep, SPEC_OPS_THEME, ELEGANT_THEME } from '../constants/themes';
import { getStepLabel } from '../constants/stepLabels';
import { ChevronLeft, ChevronRight, Save, AlertCircle } from 'lucide-react';

const StepContainer = ({ 
  step,
  children,
  onPrev,
  onNext,
  onSave,
  canGoNext = true,
  canGoPrev = true,
  isSaving = false,
  isComplete = false,
  validationErrors = [],
  showNav = true,
}) => {
  const theme = getThemeForStep(step);
  const stepInfo = getStepLabel(step);
  const isSpecOps = theme.name === 'spec-ops';
  const isElegant = theme.name === 'elegant';

  return (
    <div className={`
      min-h-[calc(100vh-200px)] flex flex-col
      ${isSpecOps ? 'bg-[#020617]' : 'bg-white'}
    `}>
      {/* Step Header */}
      <div className={`
        px-6 py-4 border-b
        ${isSpecOps ? 'border-slate-700 bg-[#0f172a]' : 'border-slate-200 bg-slate-50'}
      `}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-2xl">{stepInfo.icon}</span>
            <div>
              <h2 className={`
                text-xl font-bold tracking-tight
                ${isSpecOps ? 'text-slate-100 font-["Rajdhani"]' : 'text-slate-800'}
              `}>
                STEP {step}: {stepInfo.name.toUpperCase()}
              </h2>
              {isComplete && (
                <span className={`
                  text-xs uppercase tracking-wider
                  ${isSpecOps ? 'text-amber-400' : 'text-amber-600'}
                `}>
                  [READ ONLY - VISIT COMPLETE]
                </span>
              )}
            </div>
          </div>
          
          {/* Save button */}
          {onSave && !isComplete && (
            <button
              onClick={onSave}
              disabled={isSaving}
              className={`
                flex items-center gap-2 px-4 py-2 text-sm font-medium transition-all
                ${isSpecOps 
                  ? 'bg-emerald-600 hover:bg-emerald-500 text-white' 
                  : 'bg-blue-600 hover:bg-blue-500 text-white rounded-lg shadow-md'
                }
                ${isSaving ? 'opacity-50 cursor-wait' : ''}
              `}
            >
              <Save className="w-4 h-4" />
              {isSaving ? 'Saving...' : 'Save Progress'}
            </button>
          )}
        </div>
      </div>

      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <div className={`
          mx-6 mt-4 p-3 rounded border
          ${isSpecOps 
            ? 'bg-red-500/10 border-red-500/40 text-red-300' 
            : 'bg-red-50 border-red-200 text-red-700'
          }
        `}>
          <div className="flex items-start gap-2">
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-sm">Missing Required Fields</p>
              <ul className="mt-1 text-sm list-disc list-inside">
                {validationErrors.map((error, idx) => (
                  <li key={idx}>{error}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex-1 p-6 overflow-auto">
        {children}
      </div>

      {/* Navigation Footer */}
      {showNav && (
        <div className={`
          px-6 py-4 border-t flex items-center justify-between
          ${isSpecOps ? 'border-slate-700 bg-[#0f172a]' : 'border-slate-200 bg-slate-50'}
        `}>
          <button
            onClick={onPrev}
            disabled={!canGoPrev || step === 1}
            className={`
              flex items-center gap-2 px-4 py-2 text-sm font-medium transition-all
              ${!canGoPrev || step === 1 ? 'opacity-30 cursor-not-allowed' : ''}
              ${isSpecOps 
                ? 'border border-slate-700 text-slate-300 hover:bg-slate-800' 
                : 'border border-slate-300 text-slate-600 hover:bg-slate-100 rounded-lg'
              }
            `}
          >
            <ChevronLeft className="w-4 h-4" />
            Previous
          </button>

          <div className={`
            text-sm
            ${isSpecOps ? 'text-slate-500' : 'text-slate-400'}
          `}>
            Step {step} of 9
          </div>

          <button
            onClick={onNext}
            disabled={!canGoNext || step === 9}
            className={`
              flex items-center gap-2 px-4 py-2 text-sm font-medium transition-all
              ${!canGoNext || step === 9 ? 'opacity-30 cursor-not-allowed' : ''}
              ${isSpecOps 
                ? 'bg-emerald-600 hover:bg-emerald-500 text-white' 
                : 'bg-blue-600 hover:bg-blue-500 text-white rounded-lg shadow-md'
              }
            `}
          >
            {step === 9 ? 'Complete' : 'Next'}
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default StepContainer;
